import { v4 as Uuid } from 'uuid'
import { toRaw } from 'vue'
import { DEFAULT_DURATION } from '@/etc/consts'
import { jclone } from '@/etc/utils'
import { LOG } from '@/etc/log'
import { factory, tracksIndex } from '../state'
import { mapLocalToMedia, speedAtLocal } from '@/editor/timing/retime'
import {
  getStripRetimePointsLocal,
  isStripRetimeEnabled,
  migrateStripRetimePointsToNormalized,
  setStripRetimePointsNormalized,
} from '@/editor/timing/retimePoints'
import {
  DEFAULT_TRACK_Z_PRESET,
  isTrackZPreset,
  trackPresetBaseZ,
  clampTrackZOffset,
  sortLayerTracks,
} from '@/etc/trackZ'
import { buildFitScaleBaseline } from '@/editor/transform/fitScale'
import { getDefaultQuickbarSmartProp } from '@/editor/smart/quickbarSmart'
import {
  CLIP_ENVELOPE_OFF_PROP,
  supportsClipEnvelopeAsset,
} from '@/editor/clipEnvelopes'

const log = LOG.m('store::project::tracks')

const EPS_Z = 1e-3
const MIN_STRIP_LENGTH = 1e-3
const MIN_SPEED_MULTIPLIER = 0.01
const MAX_SPEED_MULTIPLIER = 100
const MIN_SOURCE_WINDOW = 1e-6

function finitePositive(value, fallback) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

function finiteNonNegative(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

function frameCountForStrip(strip, fps) {
  return Math.max(
    1,
    Math.round(
      finitePositive(strip?.length, MIN_STRIP_LENGTH) *
        finitePositive(fps, 24)
    )
  )
}

function normalizeStripClipRange(strip, fps) {
  const frameCount = frameCountForStrip(strip, fps)
  const current =
    strip?.clipRange && typeof strip.clipRange === 'object'
      ? strip.clipRange
      : { enabled: false, startFrame: 0, endFrameExclusive: frameCount }
  const startFrame = Math.max(
    0,
    Math.min(frameCount - 1, Math.round(finiteNonNegative(current.startFrame, 0)))
  )
  const endFrameExclusive = Math.max(
    startFrame + 1,
    Math.min(frameCount, Math.round(finitePositive(current.endFrameExclusive, frameCount)))
  )
  strip.clipRange = {
    enabled: !!current.enabled,
    startFrame,
    endFrameExclusive,
  }
  return strip.clipRange
}

function normalizeAffects(affects) {
  return affects === 'right' ? 'right' : 'left'
}

function splitAnimationKeyframes(keyframes, splitLocal, leftLen, rightLen) {
  const list = Array.isArray(keyframes) ? jclone(keyframes) : []
  const eps = 1e-6
  const left = list
    .filter((kf) => (Number(kf?.time) || 0) <= splitLocal + eps)
    .map((kf) => {
      const next = { ...kf }
      next.time = Math.max(0, Math.min(leftLen, Number(kf.time) || 0))
      return next
    })
  const right = list
    .filter((kf) => (Number(kf?.time) || 0) >= splitLocal - eps)
    .map((kf) => {
      const next = { ...kf, uuid: Uuid() }
      next.time = Math.max(0, Math.min(rightLen, (Number(kf.time) || 0) - splitLocal))
      return next
    })
  return {
    left: left.sort((a, b) => (a.time || 0) - (b.time || 0)),
    right: right.sort((a, b) => (a.time || 0) - (b.time || 0)),
  }
}

function sliceRetimePointsLocal(strip, fromLocal, toLocal, { clipLength = null, outputLength = null } = {}) {
  const oldLen = Math.max(
    MIN_STRIP_LENGTH,
    finitePositive(clipLength, finitePositive(strip?.length, MIN_STRIP_LENGTH))
  )
  const windowStart = Math.max(0, Math.min(oldLen, finiteNonNegative(fromLocal, 0)))
  const windowEnd = Math.max(windowStart, Math.min(oldLen, finiteNonNegative(toLocal, oldLen)))
  const keptLen = Math.max(0, windowEnd - windowStart)
  const nextLen = Math.max(0, finitePositive(outputLength, keptLen))
  const scale = keptLen > 0 ? (nextLen / keptLen) : 1
  const points = getStripRetimePointsLocal(strip, oldLen)
  if (!points.length || keptLen <= 0) return []

  const mode = strip?.retime?.mode === 'step' ? 'step' : 'linear'
  const eps = 1e-6
  const out = []

  const upsertPoint = (list, point) => {
    const t = Math.max(0, Math.min(nextLen, Number(point?.t) || 0))
    const speed = finitePositive(point?.speed, 1)
    const affects = normalizeAffects(point?.affects)
    const idx = list.findIndex((it) => Math.abs((Number(it.t) || 0) - t) <= eps)
    const next = { t, speed, affects }
    if (idx >= 0) list[idx] = next
    else list.push(next)
  }

  points.forEach((p) => {
    const t = Math.max(0, Math.min(oldLen, Number(p?.t) || 0))
    if (t > windowStart + eps && t < windowEnd - eps) {
      upsertPoint(out, {
        t: (t - windowStart) * scale,
        speed: p?.speed,
        affects: p?.affects
      })
    } else if (Math.abs(t - windowStart) <= eps) {
      upsertPoint(out, { t: 0, speed: p?.speed, affects: p?.affects })
    } else if (Math.abs(t - windowEnd) <= eps) {
      upsertPoint(out, { t: nextLen, speed: p?.speed, affects: p?.affects })
    }
  })

  // Preserve source-continuous boundary behavior for the kept local window.
  const sampleStart = mode === 'step'
    ? Math.min(oldLen, windowStart + 1e-5)
    : windowStart
  const sampleEnd = mode === 'step'
    ? Math.max(0, windowEnd - 1e-5)
    : windowEnd
  const speedStart = finitePositive(speedAtLocal(sampleStart, oldLen, points, mode), 1)
  const speedEnd = finitePositive(speedAtLocal(sampleEnd, oldLen, points, mode), 1)
  if (mode === 'step') {
    // Start boundary should describe kept-right segment speed.
    upsertPoint(out, { t: 0, speed: speedStart, affects: 'right' })
    // End boundary should describe kept-left segment speed.
    upsertPoint(out, { t: nextLen, speed: speedEnd, affects: 'left' })
  } else {
    upsertPoint(out, { t: 0, speed: speedStart, affects: 'left' })
    upsertPoint(out, { t: nextLen, speed: speedEnd, affects: 'left' })
  }

  return out.sort((a, b) => a.t - b.t)
}

function clipSourceCoverage(strip) {
  const length = Math.max(MIN_STRIP_LENGTH, finitePositive(strip?.length, MIN_STRIP_LENGTH))
  const timeScale = finitePositive(strip?.timeScale, 1)
  const retimeEnabled = isStripRetimeEnabled(strip)
  const points = retimeEnabled ? getStripRetimePointsLocal(strip, length) : []
  const mode = typeof strip?.retime?.mode === 'string' ? strip.retime.mode : 'linear'
  if (!points.length) return Math.max(MIN_STRIP_LENGTH, length * timeScale)
  try {
    const coverage = mapLocalToMedia(length, length, points, mode)
    return Math.max(MIN_STRIP_LENGTH, finitePositive(coverage, length * timeScale))
  } catch (e) {
    return Math.max(MIN_STRIP_LENGTH, length * timeScale)
  }
}

function mediaDurationCap(strip) {
  const cap = Number(strip?.origDuration)
  return Number.isFinite(cap) && cap > 0 ? cap : null
}

function clampSourceWindow(strip) {
  if (!strip) return
  const cap = mediaDurationCap(strip)
  const sourceStart = finiteNonNegative(strip.sourceStart, 0)
  strip.sourceStart = cap != null ? Math.min(sourceStart, Math.max(0, cap)) : sourceStart
  let sourceDuration = finitePositive(strip.sourceDuration, clipSourceCoverage(strip))
  if (cap != null) {
    const maxDur = Math.max(MIN_STRIP_LENGTH, cap - strip.sourceStart)
    sourceDuration = Math.max(MIN_STRIP_LENGTH, Math.min(sourceDuration, maxDur))
  }
  strip.sourceDuration = sourceDuration
}

function syncStripTimingModel(strip, { forceScale = false } = {}) {
  if (!strip) return
  const length = Math.max(MIN_STRIP_LENGTH, finitePositive(strip.length, MIN_STRIP_LENGTH))
  strip.length = length
  if (strip.retime) migrateStripRetimePointsToNormalized(strip, length)
  clampSourceWindow(strip)
  const retimeEnabled = isStripRetimeEnabled(strip)
  const existingScale = finitePositive(strip.timeScale, NaN)
  const derivedScale = Math.max(MIN_SPEED_MULTIPLIER, strip.sourceDuration / length)
  // In curve mode, timeScale is compatibility data derived from the source window.
  if (retimeEnabled || forceScale || !Number.isFinite(existingScale)) {
    strip.timeScale = derivedScale
  } else {
    strip.timeScale = existingScale
  }
}

function ensureStripTimingModel(state) {
  if (!state || !state.tracks) return
  Object.keys(state.tracks).forEach((tuuid) => {
    const track = state.tracks[tuuid]
    if (!track || !track.strips) return
    Object.keys(track.strips).forEach((suuid) => {
      const strip = track.strips[suuid]
      if (!strip) return
      if (strip.retime) migrateStripRetimePointsToNormalized(strip, strip.length)
      if (!Number.isFinite(Number(strip.sourceStart))) strip.sourceStart = 0
      if (!Number.isFinite(Number(strip.sourceDuration)) || Number(strip.sourceDuration) <= 0) {
        strip.sourceDuration = clipSourceCoverage(strip)
      }
      syncStripTimingModel(strip)
    })
  })
}

function ensureAnimationValues(state) {
  if (!state || !state.tracks) return
  Object.keys(state.tracks).forEach((tuuid) => {
    const track = state.tracks[tuuid]
    if (!track || !track.strips) return
    Object.keys(track.strips).forEach((suuid) => {
      const strip = track.strips[suuid]
      if (!strip || !strip.animation) return
      if (strip.animation.values && typeof strip.animation.values === 'object')
        return
      const asset = strip.asset && strip.asset.uuid ? factory.asset(strip.asset.uuid) : null
      const def = asset?.defaultProps || {}
      strip.animation.values = { ...def }
    })
  })
}

function ensureTrackZPreset(state) {
  if (!state || !state.settings) return
  const cur = state.settings.trackZPreset
  if (!isTrackZPreset(cur)) state.settings.trackZPreset = DEFAULT_TRACK_Z_PRESET
}

function ensureTrackZOffsets(state) {
  if (!state || !state.tracks) return
  Object.keys(state.tracks).forEach((tuuid) => {
    const t = state.tracks[tuuid]
    if (!t || t.type !== 'layer') return
    const z = Number(t.zOffset)
    if (!Number.isFinite(z)) t.zOffset = 0
  })
  // Track 1 is the reference plane for track-level Z (fixed at 0)
  if (state.tracks[tracksIndex.trackUuid1]) state.tracks[tracksIndex.trackUuid1].zOffset = 0
}

function effectiveTrackZ(state, track) {
  if (!state || !track) return 0
  const preset = state.settings?.trackZPreset
  const baseZ = trackPresetBaseZ(preset, track.layer)
  const isTrack1 = track.uuid === tracksIndex.trackUuid1
  const off = isTrack1 ? 0 : (Number.isFinite(Number(track.zOffset)) ? Number(track.zOffset) : 0)
  return baseZ + off
}

function clampAllLayerTrackZOffsets(state) {
  if (!state || !state.tracks) return
  ensureTrackZPreset(state)
  ensureTrackZOffsets(state)

  const layers = sortLayerTracks(state.tracks)
  if (!layers.length) return

  // Pass 1: clamp against previous (closest) track only
  let prevZ = null
  layers.forEach((t) => {
    if (!t) return
    const baseZ = trackPresetBaseZ(state.settings.trackZPreset, t.layer)
    const isTrack1 = t.uuid === tracksIndex.trackUuid1
    const desired = isTrack1 ? 0 : (Number.isFinite(Number(t.zOffset)) ? Number(t.zOffset) : 0)
    const clamped = isTrack1
      ? 0
      : clampTrackZOffset({ baseZ, desiredOffset: desired, prevZ, nextZ: null, eps: EPS_Z })
    t.zOffset = clamped
    prevZ = baseZ + (isTrack1 ? 0 : clamped)
  })

  // Pass 2: clamp against next (farther) track only
  let nextZ = null
  for (let i = layers.length - 1; i >= 0; i--) {
    const t = layers[i]
    if (!t) continue
    const baseZ = trackPresetBaseZ(state.settings.trackZPreset, t.layer)
    const isTrack1 = t.uuid === tracksIndex.trackUuid1
    const desired = isTrack1 ? 0 : (Number.isFinite(Number(t.zOffset)) ? Number(t.zOffset) : 0)
    const clamped = isTrack1
      ? 0
      : clampTrackZOffset({ baseZ, desiredOffset: desired, prevZ: null, nextZ, eps: EPS_Z })
    t.zOffset = clamped
    nextZ = baseZ + (isTrack1 ? 0 : clamped)
  }
}

export default {
  redefineTracks: (state, next) => {
    state.tracks = next
    // Phase 2: ensure per-strip base values exist for V2 (no-keyframes baseline)
    ensureAnimationValues(state)
    // Phase 1: migrate legacy strips to canonical source/output timing model.
    ensureStripTimingModel(state)
    // Phase 2: normalize track-level Z metadata for older projects
    ensureTrackZPreset(state)
    ensureTrackZOffsets(state)
    clampAllLayerTrackZOffsets(state)
  },
  defineSceneCamera: (state, { cameraStrip }) => {
    // Legacy name retained; scene is no longer a timeline strip.
    state.cameraStrip = cameraStrip && typeof cameraStrip === 'object'
      ? cameraStrip.uuid
      : cameraStrip
  },
  addStrip: (state, { auuid, tuuid, time, depth = -1, uuid = undefined, length: overrideLength = undefined }) => {
    if (state._pushUndo) state._pushUndo('addStrip')
    // console.log('add strip', auuid, tuuid, time)
    const asset = factory.asset(auuid)
    if (!asset) return
    if (!state.tracks[tuuid]) return
    let d = state.tracks[tuuid].depth
    if (depth >= 0) d = depth
    const baseLen = asset.meta.duration || DEFAULT_DURATION
    const len = (typeof overrideLength === 'number' && Number.isFinite(overrideLength) && overrideLength > 0)
      ? overrideLength
      : baseLen
    const animation = asset.initAnimation(
      toRaw(state.settings),
      toRaw(state.timeline.zeroFrame3D)
    )
    const baseValues = { ...(asset.defaultProps || {}) }
    const transformFitBaseline = buildFitScaleBaseline({
      x: baseValues['scale.x'],
      y: baseValues['scale.y'],
      z: baseValues['scale.z'],
    })
    const strip = {
      uuid: uuid || Uuid(),
      track: tuuid,
      asset: asset.toObject(),
      start: finiteNonNegative(time, 0),
      depth: d,
      length: len,
      // Phase 1: canonical source window (media-domain)
      sourceStart: 0,
      sourceDuration: len,
      // Preserve original media duration for reference (Phase 0)
      origDuration: baseLen,
      // Per-strip playback rate; 1.0 means normal speed (Phase 0)
      timeScale: 1,
      // Phase 4+: Retime lane scaffold is now on by default for new strips.
      retime: {
        enabled: true,
        mode: 'step',
        // points: array of { u: normalizedLocal[0..1], speed: multiplier }
        points: []
      },
      // Phase 4: Smart Key Groups container
      smartKeys: {},
      clipRange: {
        enabled: false,
        startFrame: 0,
        endFrameExclusive: Math.max(
          1,
          Math.round(len * finitePositive(state.settings?.fps, 24))
        ),
      },
      animation: {
        ...(animation || { uuid: Uuid(), keyframes: [] }),
        values: baseValues
      },
      transformFitBaseline,
      effects: {},
      scene: null,
    }
    if (supportsClipEnvelopeAsset(asset)) {
      strip.clipEnvelopeProp = CLIP_ENVELOPE_OFF_PROP
      strip.clipEnvelopes = {}
    }
    normalizeStripClipRange(strip, state.settings?.fps)
    // Phase 2: seed Smart Key Groups by default for numeric props (gated by setting)
    try {
      const globalSmartOn = !!state.settings.smartKeyGroupsEnabled
      const textAlwaysOn = asset.type === 'text'
      const shouldSeed = textAlwaysOn || (globalSmartOn && (asset.type === 'adjustment' || asset.type === 'camera'))
      if (!shouldSeed) throw new Error('SmartKeys disabled for this strip type')
      const def = asset.defaultProps || {}
      const entryProps = (strip.animation && strip.animation.keyframes && strip.animation.keyframes[0]
        ? (strip.animation.keyframes[0].props || {})
        : {})
      const numeric = Object.keys(def).filter(k => typeof def[k] === 'number')
      strip.smartKeys = numeric.reduce((acc, prop) => {
        const v = (typeof entryProps[prop] === 'number') ? entryProps[prop] : def[prop]
        acc[prop] = {
          start: { value: v },
          left: { value: v },
          right: { value: v },
          end: { value: v },
          offsets: { entry: 0, exit: 0 },
          links: { rightToLeft: false },
          sustainEasing: 'Linear.None',
          entryEasing: 'Linear.None',
          exitEasing: 'Linear.None'
        }
        return acc
      }, {})

      // Phase 3b: Seed per-axis microByRegion scaffolding for Text strips (2D + text always-on SmartKeys).
      // This ensures Position/Scale/Rotation modulation UI works immediately on new strips.
      if (textAlwaysOn) {
        strip.textEnvelopeUiVersion = 1
        const regions = ['entry', 'sustain', 'exit']
        const makeMicroByRegion = () => {
          const microByRegion = {}
          regions.forEach(r => {
            microByRegion[r] = {
              region: r,
              // Effects-style rate controls (TextModal edits these)
              rateUnit: 'quarter',
              dotted: false,
              triplet: false,
              phaseBeats: 0,
              // Derived cadence fields (kept for evaluator compatibility)
              every: { unit: 'beats', value: 1 },
              span: { unit: 'beats', value: 1 },
              beatLocked: true,
              beatLockScope: 'global',
              instancesEnabled: false,
              instances: 1,
              duty: 1,
              amp: 1, // enabled by default but window 0 means no movement
              shape: 'windowRelative',
              linked: true,
              window: { low: 0, high: 0 },
              radius: 0, // legacy fallback; prefer window.low/high
              yoyo: true,
            }
          })
          return microByRegion
        }
        ;['position.x', 'position.y', 'scale.x', 'scale.y', 'rotation.z'].forEach((prop) => {
          const g = strip.smartKeys[prop]
          if (!g || typeof g !== 'object') return
          if (g.microByRegion && typeof g.microByRegion === 'object') return
          strip.smartKeys[prop] = { ...g, microByRegion: makeMicroByRegion() }
        })
      }

      // Phase 2 (matrix): seed a single 'transform' Smart Key group for transformable assets
      const hasTransform = ['position.x','position.y','position.z','rotation.x','rotation.y','rotation.z','scale.x','scale.y','scale.z']
        .every(k => typeof def[k] === 'number')
      if (hasTransform) {
        const p = {
          x: (typeof entryProps['position.x'] === 'number') ? entryProps['position.x'] : def['position.x'],
          y: (typeof entryProps['position.y'] === 'number') ? entryProps['position.y'] : def['position.y'],
          z: (typeof entryProps['position.z'] === 'number') ? entryProps['position.z'] : def['position.z']
        }
        const r = {
          x: (typeof entryProps['rotation.x'] === 'number') ? entryProps['rotation.x'] : def['rotation.x'],
          y: (typeof entryProps['rotation.y'] === 'number') ? entryProps['rotation.y'] : def['rotation.y'],
          z: (typeof entryProps['rotation.z'] === 'number') ? entryProps['rotation.z'] : def['rotation.z']
        }
        const s = {
          x: (typeof entryProps['scale.x'] === 'number') ? entryProps['scale.x'] : def['scale.x'],
          y: (typeof entryProps['scale.y'] === 'number') ? entryProps['scale.y'] : def['scale.y'],
          z: (typeof entryProps['scale.z'] === 'number') ? entryProps['scale.z'] : def['scale.z']
        }
        strip.smartKeys.transform = {
          start: { position: { ...p }, rotation: { ...r }, scale: { ...s } },
          left: { position: { ...p }, rotation: { ...r }, scale: { ...s } },
          right: { position: { ...p }, rotation: { ...r }, scale: { ...s } },
          end: { position: { ...p }, rotation: { ...r }, scale: { ...s } },
          offsets: { entry: 0, exit: 0 },
          links: { rightToLeft: true },
          sustainEasing: 'Linear.None',
          entryEasing: 'Linear.None',
          exitEasing: 'Linear.None'
        }
        // Prefer QuickBar default when available; otherwise keep transform for non-QuickBar strip types.
        strip.envelopeProp = getDefaultQuickbarSmartProp(asset) || 'transform'
      }
    } catch (e) {
      // when disabled or on error, keep empty smartKeys; evaluator will fallback to V2 keyframes
    }
    if (!state.tracks[tuuid]) {
      log.err('track to add strip not found', strip)
      return
    }
    state.tracks[tuuid].strips[strip.uuid] = strip
    if (state.tracks[tuuid].maxDepth < d) state.tracks[tuuid].maxDepth = d
    state.timeline.activeStrip = strip.uuid
    state.timeline.activeTrack = tuuid
    state.timeline.activeKeyframe = null
    state.tool = []

    if (!state.timeline.activeTrack || !state.timeline.activeStrip) return
    state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].animation.keyframes.forEach((kf) => {
      if (
        state.tracks[state.timeline.activeTrack].strips[
          state.timeline.activeStrip
        ].start +
          kf.time ===
        state.timeline.currentTime
      )
        state.timeline.activeKeyframe = kf.uuid
    })
    syncStripTimingModel(strip, { forceScale: true })
    state.tuned = false
  },
  moveStrip: (state, { suuid, depth, tuuid, tfuuid, time }) => {
    state.timeline.activeStrip = suuid
    state.timeline.activeTrack = tuuid
    if (tuuid === tfuuid) {
      const s = state.tracks[tuuid].strips[suuid]
      s.depth = finiteNonNegative(depth, finiteNonNegative(s.depth, 0))
      s.start = finiteNonNegative(time, finiteNonNegative(s.start, 0))
      return
    }
    const strip = state.tracks[tfuuid].strips[suuid]
    strip.track = tuuid
    strip.start = finiteNonNegative(time, finiteNonNegative(strip.start, 0))
    strip.depth = finiteNonNegative(depth, finiteNonNegative(strip.depth, 0))
    state.tracks[tuuid].strips[suuid] = strip
    delete state.tracks[tfuuid].strips[suuid]
    state.tuned = false
  },
  removeStrip: (state, { strip, track, force = false } = {}) => {
    if (!strip || !track) return
    // Camera strip is required (auto-managed). Block deletion unless explicitly forced.
    if (!force && state.cameraStrip && strip === state.cameraStrip) return
    if (!state.tracks[track]) return
    delete state.tracks[track].strips[strip]
    state.tuned = false
  },
  // setStripScene removed (scene is now project-level settings; PPE uses adjustment layers)
  // Deprecated for regular trims: kept for compatibility where needed
  resizeActiveStrip: (state, { start, length }) => {
    if (
      !state.timeline.activeTrack ||
      !state.tracks[state.timeline.activeTrack]
    )
      return
    if (
      !state.timeline.activeStrip ||
      !state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ]
    )
      return
    const strip =
      state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ]
    const oldLen = Math.max(MIN_STRIP_LENGTH, finitePositive(strip.length, MIN_STRIP_LENGTH))
    const oldSourceDuration = finitePositive(strip.sourceDuration, clipSourceCoverage(strip))
    const retimeEnabled = isStripRetimeEnabled(strip)
    const ratio = length / strip.length
    state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].start = finiteNonNegative(start, finiteNonNegative(strip.start, 0))
    state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].length = length
    const kfs = jclone(strip.animation.keyframes)
    state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].animation.keyframes = kfs.map((kf) => {
      kf.time *= ratio
      Object.keys(kf.delay).forEach((prop) => {
        kf.delay[prop] *= ratio
      })
      return kf
    })
    if (!retimeEnabled) {
      const nextLen = Math.max(MIN_STRIP_LENGTH, finitePositive(length, oldLen))
      const nextDuration = Math.max(MIN_STRIP_LENGTH, oldSourceDuration * (nextLen / oldLen))
      strip.sourceDuration = nextDuration
    }
    syncStripTimingModel(strip, { forceScale: !retimeEnabled })
    state.tuned = false
  },
  // Phase 2: Regular trim without proportional keyframe scaling
  trimActiveStrip: (state, { start, length }) => {
    if (state._pushUndo) state._pushUndo()
    if (!state.timeline.activeTrack || !state.tracks[state.timeline.activeTrack])
      return
    if (!state.timeline.activeStrip || !state.tracks[state.timeline.activeTrack].strips[state.timeline.activeStrip])
      return
    const strip = state.tracks[state.timeline.activeTrack].strips[state.timeline.activeStrip]
    const oldStart = finiteNonNegative(strip.start, 0)
    const oldLen = Math.max(MIN_STRIP_LENGTH, finitePositive(strip.length, MIN_STRIP_LENGTH))
    const oldTs = finitePositive(strip.timeScale, 1)
    const oldSourceStart = finiteNonNegative(strip.sourceStart, 0)
    const oldSourceDuration = finitePositive(strip.sourceDuration, clipSourceCoverage(strip))
    const retimeEnabled = isStripRetimeEnabled(strip)
    const mode = strip?.retime?.mode === 'step' ? 'step' : 'linear'
    const oldPoints = retimeEnabled ? getStripRetimePointsLocal(strip, oldLen) : []
    const nextLen = Math.max(MIN_STRIP_LENGTH, finitePositive(length, oldLen))
    const nextStart = finiteNonNegative(start, oldStart)
    const dStart = nextStart - oldStart
    const dLen = nextLen - oldLen
    const keepStartLocal = Math.max(0, Math.min(oldLen, nextStart - oldStart))
    const keepEndLocal = Math.max(
      keepStartLocal + MIN_STRIP_LENGTH,
      Math.min(oldLen, keepStartLocal + nextLen)
    )
    // Update bounds only
    strip.start = nextStart
    strip.length = nextLen
    // Phase 1: keep source window in sync for linear trims.
    // (Retime lane strips keep current source window; later phases will refine per-point trim behavior.)
    if (!retimeEnabled) {
      strip.sourceStart = oldSourceStart + (dStart * oldTs)
      strip.sourceDuration = oldSourceDuration + (dLen * oldTs)
      clampSourceWindow(strip)
      strip.timeScale = Math.max(MIN_SPEED_MULTIPLIER, strip.sourceDuration / Math.max(MIN_STRIP_LENGTH, strip.length))
    } else {
      const totalCoverageRaw = Math.max(
        MIN_SOURCE_WINDOW,
        finitePositive(mapLocalToMedia(oldLen, oldLen, oldPoints, mode), oldSourceDuration)
      )
      const rangeStartRaw = Math.max(
        0,
        Math.min(totalCoverageRaw, finiteNonNegative(mapLocalToMedia(keepStartLocal, oldLen, oldPoints, mode), 0))
      )
      const rangeEndRaw = Math.max(
        rangeStartRaw,
        Math.min(totalCoverageRaw, finiteNonNegative(mapLocalToMedia(keepEndLocal, oldLen, oldPoints, mode), totalCoverageRaw))
      )
      const keepCoverageRaw = Math.max(0, rangeEndRaw - rangeStartRaw)
      const coverageScale = oldSourceDuration > 0 && totalCoverageRaw > 0
        ? (oldSourceDuration / totalCoverageRaw)
        : 1
      strip.sourceStart = oldSourceStart + (rangeStartRaw * coverageScale)
      if (retimeEnabled) {
        const trimmedPoints = sliceRetimePointsLocal(strip, keepStartLocal, keepEndLocal, {
          clipLength: oldLen,
          outputLength: nextLen,
        })
        setStripRetimePointsNormalized(strip, trimmedPoints, nextLen)
        const remappedCoverageRaw = Math.max(
          MIN_SOURCE_WINDOW,
          finitePositive(mapLocalToMedia(nextLen, nextLen, trimmedPoints, mode), keepCoverageRaw)
        )
        strip.sourceDuration = Math.max(MIN_SOURCE_WINDOW, remappedCoverageRaw * coverageScale)
      } else {
        strip.sourceDuration = Math.max(MIN_SOURCE_WINDOW, keepCoverageRaw * coverageScale)
      }
      syncStripTimingModel(strip)
    }
    // Clamp any keyframe beyond new length; in V2 do not rely on 'exit'
    const v2 = !!state.settings.legacyKeyframesV2
    const kfs = jclone(strip.animation.keyframes)
    for (let i = 0; i < kfs.length; i++) {
      if (!v2 && kfs[i].type === 'exit') {
        kfs[i].time = nextLen
      }
      if (kfs[i].time > nextLen) {
        kfs[i].time = nextLen
      }
    }
    strip.animation.keyframes = kfs
    state.tuned = false
  },
  // Phase 2: Speed trim via time mapping; do not alter keyframes
  speedTrimActiveStrip: (state, { anchor = 'back', start, length }) => {
    if (state._pushUndo) state._pushUndo()
    if (!state.timeline.activeTrack || !state.tracks[state.timeline.activeTrack])
      return
    if (!state.timeline.activeStrip || !state.tracks[state.timeline.activeTrack].strips[state.timeline.activeStrip])
      return
    const strip = state.tracks[state.timeline.activeTrack].strips[state.timeline.activeStrip]
    const oldLen = Number(strip.length) || 0
    const sourceDuration = finitePositive(strip.sourceDuration, clipSourceCoverage(strip))
    const minLen = Math.max(MIN_STRIP_LENGTH, sourceDuration / MAX_SPEED_MULTIPLIER)
    const maxLen = Math.max(minLen, sourceDuration / MIN_SPEED_MULTIPLIER)
    const requestedLen = finitePositive(length, oldLen || MIN_STRIP_LENGTH)
    const newLen = Math.max(minLen, Math.min(requestedLen, maxLen))
    // Phase 1: absolute speed model from source window (non-recursive).
    const ratio = sourceDuration / newLen
    const keyScale = oldLen > 0 ? (newLen / oldLen) : 1
    // Update start/length according to the anchor
    if (anchor === 'front') {
      // keep right edge anchored; start comes from UI/hologram
      strip.start = finiteNonNegative(start, finiteNonNegative(strip.start, 0))
      strip.length = newLen
    } else {
      // back anchor by default; start stays, only length changes
      strip.length = newLen
    }
    // Phase 2 (traditional editors): when speed changes length, keyframes compress/expand with the clip.
    // Scale keyframe times and delays proportionally so relative placement stays the same.
    if (strip.animation && Array.isArray(strip.animation.keyframes) && Number.isFinite(keyScale) && keyScale > 0 && keyScale !== 1) {
      const kfs = jclone(strip.animation.keyframes).map((kf) => {
        const next = { ...kf }
        next.time = Math.max(0, Math.min(newLen, (Number(kf.time) || 0) * keyScale))
        const delay = (kf.delay && typeof kf.delay === 'object') ? kf.delay : {}
        next.delay = { ...(delay || {}) }
        Object.keys(next.delay).forEach((prop) => {
          const d = Number(next.delay[prop]) || 0
          next.delay[prop] = Math.max(0, d * keyScale)
        })
        return next
      })
      strip.animation.keyframes = kfs.sort((a, b) => (a.time || 0) - (b.time || 0))
    }
    strip.sourceDuration = sourceDuration
    strip.timeScale = Number.isFinite(ratio) && ratio > 0 ? ratio : 1
    syncStripTimingModel(strip)
    state.tuned = false
  },
  splitActiveStripAtPlayhead: (state) => {
    if (state._pushUndo) state._pushUndo('splitStrip')
    const tuuid = state.timeline.activeTrack
    const suuid = state.timeline.activeStrip
    const track = tuuid ? state.tracks[tuuid] : null
    const strip = track?.strips?.[suuid]
    if (!track || !strip) return
    if (strip.asset?.type === 'camera') return

    const oldStart = finiteNonNegative(strip.start, 0)
    const oldLen = Math.max(MIN_STRIP_LENGTH, finitePositive(strip.length, MIN_STRIP_LENGTH))
    const cutGlobal = finiteNonNegative(state.timeline.currentTime, oldStart)
    const cutLocalRaw = cutGlobal - oldStart
    const cutLocal = Math.max(MIN_STRIP_LENGTH, Math.min(oldLen - MIN_STRIP_LENGTH, cutLocalRaw))
    if (cutLocal <= MIN_STRIP_LENGTH || cutLocal >= oldLen - MIN_STRIP_LENGTH) return

    const leftLen = cutLocal
    const rightLen = Math.max(MIN_STRIP_LENGTH, oldLen - cutLocal)
    const original = jclone(strip)
    const retimeEnabled = isStripRetimeEnabled(original)
    const mode = original?.retime?.mode === 'step' ? 'step' : 'linear'
    const oldPoints = retimeEnabled ? getStripRetimePointsLocal(original, oldLen) : []

    const oldSourceStart = finiteNonNegative(original.sourceStart, 0)
    const oldSourceDuration = finitePositive(original.sourceDuration, clipSourceCoverage(original))

    let totalCoverageRaw = retimeEnabled
      ? mapLocalToMedia(oldLen, oldLen, oldPoints, mode)
      : (oldLen * finitePositive(original.timeScale, 1))
    totalCoverageRaw = Math.max(MIN_SOURCE_WINDOW, finitePositive(totalCoverageRaw, oldSourceDuration))

    let leftCoverageRaw = retimeEnabled
      ? mapLocalToMedia(cutLocal, oldLen, oldPoints, mode)
      : (cutLocal * finitePositive(original.timeScale, 1))
    leftCoverageRaw = Math.max(0, Math.min(totalCoverageRaw, finiteNonNegative(leftCoverageRaw, 0)))

    const coverageScale = oldSourceDuration > 0 && totalCoverageRaw > 0
      ? (oldSourceDuration / totalCoverageRaw)
      : 1
    let leftSourceDuration = Math.max(MIN_SOURCE_WINDOW, leftCoverageRaw * coverageScale)
    leftSourceDuration = Math.min(
      Math.max(MIN_SOURCE_WINDOW, oldSourceDuration - MIN_SOURCE_WINDOW),
      leftSourceDuration
    )
    let rightSourceDuration = Math.max(MIN_SOURCE_WINDOW, oldSourceDuration - leftSourceDuration)
    // Keep exact conservation (within floating tolerance).
    leftSourceDuration = Math.max(MIN_SOURCE_WINDOW, oldSourceDuration - rightSourceDuration)

    const splitKfs = splitAnimationKeyframes(original?.animation?.keyframes, cutLocal, leftLen, rightLen)
    const leftPoints = retimeEnabled ? sliceRetimePointsLocal(original, 0, cutLocal) : []
    const rightPoints = retimeEnabled ? sliceRetimePointsLocal(original, cutLocal, oldLen) : []

    // Left/original child
    strip.length = leftLen
    strip.sourceStart = oldSourceStart
    strip.sourceDuration = leftSourceDuration
    if (strip.animation && Array.isArray(strip.animation.keyframes)) {
      strip.animation.keyframes = splitKfs.left
    }
    if (retimeEnabled) {
      strip.retime = { ...strip.retime }
      setStripRetimePointsNormalized(strip, leftPoints, leftLen)
    }
    syncStripTimingModel(strip)

    // Right/new child
    const rightStrip = jclone(original)
    rightStrip.uuid = Uuid()
    rightStrip.track = tuuid
    rightStrip.start = oldStart + cutLocal
    rightStrip.length = rightLen
    rightStrip.sourceStart = oldSourceStart + leftSourceDuration
    rightStrip.sourceDuration = rightSourceDuration
    rightStrip.animation = {
      ...(rightStrip.animation || {}),
      uuid: Uuid(),
      keyframes: splitKfs.right,
    }
    if (retimeEnabled) {
      rightStrip.retime = { ...rightStrip.retime }
      setStripRetimePointsNormalized(rightStrip, rightPoints, rightLen)
    }
    syncStripTimingModel(rightStrip)
    track.strips[rightStrip.uuid] = rightStrip

    state.timeline.activeTrack = tuuid
    state.timeline.activeStrip = rightStrip.uuid
    state.timeline.activeKeyframe = null
    state.tuned = false
  },
  updateProjectBPM: (state, sourceAssetUuid) => {
    const asset = factory.asset(sourceAssetUuid)
    // console.log('updateBPM', asset)
    if (!asset || asset.type !== 'audio') {
      log.warn(
        'audio asset to update project bpm not found!',
        sourceAssetUuid,
        asset
      )
      return
    }
    state.settings.bpm = asset.meta.bpm
    state.tuned = false
  },
  updateProjectLength: (state) => {
    const v2 = !!state.settings.legacyKeyframesV2
    const camStripUuid = state.cameraStrip
    const ignoredStripUuids = new Set(
      [camStripUuid].filter((x) => typeof x === 'string' && x)
    )
    const tracksLen = Object.keys(state.tracks).reduce((acc, tuuid) => {
      const track = state.tracks[tuuid]
      // Ignore the hidden global rig track when computing project duration.
      if (track && track.type === 'global') return acc
      const tmax = Object.keys(state.tracks[tuuid].strips).reduce(
        (tacc, suuid) => {
          const strip = state.tracks[tuuid].strips[suuid]
          if (!strip) return tacc
          // Also ignore camera strip regardless of which track it lives on.
          if (ignoredStripUuids.has(suuid)) return tacc
          if (strip.asset && strip.asset.type === 'camera') return tacc
          return Math.max(
            tacc,
            strip.start + strip.length
          )
        },
        0
      )
      return Math.max(acc, tmax)
    }, 0)
    state.timeline.length = tracksLen
    if (state.timeline.exportAuto) {
      const limit = Math.max(tracksLen, state.timeline.viewMax || tracksLen)
      state.timeline.exportOut = Math.max(state.timeline.exportIn || 0, limit)
    }
    // update camera/any other "global full length" strips length
    const updStrip = (tuuid, suuid, newLen) => {
      const uStrip = state.tracks[tuuid].strips[suuid]
      if (!uStrip) return
      // console.log('updStrip', tuuid, suuid, newLen, uStrip)
      if (uStrip.length === newLen) return
      state.tracks[tuuid].strips[suuid].length = newLen
      // console.log('uStrip.length', uStrip.length, newLen)
      // const sizeDiff = newLen - uStrip.length
      let kfiLast = -1
      for (let i = 0; i < uStrip.animation.keyframes.length; i++) {
        const kf = uStrip.animation.keyframes[i]
        if (!v2 && kf.type === 'exit') {
          state.tracks[tuuid].strips[suuid].animation.keyframes[i].time =
            newLen
          continue
        }
        if (kf.type === 'point') {
          if (i > kfiLast) kfiLast = i
          if (kf.time > newLen) {
            state.tracks[tuuid].strips[suuid].animation.keyframes[i].time =
              newLen
            Object.keys(kf.delay || {}).forEach((prop) => {
              state.tracks[tuuid].strips[suuid].animation.keyframes[i].delay[
                prop
              ] = 0
            })
          }
        }
      }
      if (kfiLast > -1) {
        const kf =
          state.tracks[tuuid].strips[suuid].animation.keyframes[kfiLast]
        Object.keys(kf.delay).forEach((prop) => {
          if (kf.time + kf.delay[prop] > newLen) {
            const diff = kf.time + kf.delay[prop] - newLen
            // console.log('delay diff', diff)
            state.tracks[tuuid].strips[suuid].animation.keyframes[
              kfiLast
            ].delay[prop] = kf.delay[prop] - diff
          }
        })
      }
    }
    const findTrackForStrip = (suuid) => {
      if (!suuid) return null
      return Object.keys(state.tracks).find((tuuid) => {
        const t = state.tracks[tuuid]
        return !!(t && t.strips && t.strips[suuid])
      }) || null
    }

    // Prefer updating all strips on the global rig track.
    Object.keys(state.tracks).forEach((tuuid) => {
      const t = state.tracks[tuuid]
      if (t && t.type === 'global') {
        Object.keys(t.strips || {}).forEach((suuid) => {
          updStrip(tuuid, suuid, tracksLen)
        })
      }
    })

    if (camStripUuid) {
      const tuuid = findTrackForStrip(camStripUuid)
      if (tuuid) updStrip(tuuid, camStripUuid, tracksLen)
    }
    state.tuned = false
  },
  setStripClipRangeLive: (state, { tuuid, suuid, range, enabled = true } = {}) => {
    const track = state.tracks[tuuid]
    if (!track) return
    const strip = track.strips[suuid]
    if (!strip) return
    strip.clipRange = {
      ...(strip.clipRange || {}),
      ...(range || {}),
      enabled: !!enabled,
    }
    normalizeStripClipRange(strip, state.settings?.fps)
    state.tuned = false
    state.saved = false
  },
  setStripClipRange: (state, { tuuid, suuid, range, enabled = true } = {}) => {
    if (state._pushUndo) state._pushUndo('setStripClipRange')
    const track = state.tracks[tuuid]
    if (!track) return
    const strip = track.strips[suuid]
    if (!strip) return
    strip.clipRange = {
      ...(strip.clipRange || {}),
      ...(range || {}),
      enabled: !!enabled,
    }
    normalizeStripClipRange(strip, state.settings?.fps)
    state.tuned = false
    state.saved = false
  },
  clearStripClipRange: (state, { tuuid, suuid } = {}) => {
    if (state._pushUndo) state._pushUndo('clearStripClipRange')
    const track = state.tracks[tuuid]
    if (!track) return
    const strip = track.strips[suuid]
    if (!strip) return
    strip.clipRange = {
      enabled: false,
      startFrame: 0,
      endFrameExclusive: frameCountForStrip(strip, state.settings?.fps),
    }
    state.tuned = false
    state.saved = false
  },
  setTrackProp: (state, { uuid, code, value }) => {
    if (!state.tracks[uuid]) return
    state.tracks[uuid][code] = value
    state.tuned = false
  },
  setTrackZPreset: (state, preset) => {
    if (state._pushUndo) state._pushUndo('setTrackZPreset')
    if (!state.settings) return
    const next = isTrackZPreset(preset) ? preset : DEFAULT_TRACK_Z_PRESET
    state.settings.trackZPreset = next
    clampAllLayerTrackZOffsets(state)
    state.tuned = false
    state.saved = false
  },
  setTrackZOffset: (state, { uuid, value }) => {
    if (state._pushUndo) state._pushUndo('setTrackZOffset')
    if (!state.tracks || !state.tracks[uuid]) return
    ensureTrackZPreset(state)
    ensureTrackZOffsets(state)

    // Track 1 fixed at zOffset=0
    if (uuid === tracksIndex.trackUuid1) {
      state.tracks[uuid].zOffset = 0
      state.tuned = false
      state.saved = false
      return
    }

    const layers = sortLayerTracks(state.tracks)
    const idx = layers.findIndex((t) => t && t.uuid === uuid)
    if (idx < 0) return

    const t = layers[idx]
    const baseZ = trackPresetBaseZ(state.settings.trackZPreset, t.layer)
    const prev = idx > 0 ? layers[idx - 1] : null
    const next = idx < layers.length - 1 ? layers[idx + 1] : null
    const prevZ = prev ? effectiveTrackZ(state, prev) : null
    const nextZ = next ? effectiveTrackZ(state, next) : null

    const desired = Number(value)
    const desiredOffset = Number.isFinite(desired) ? desired : 0
    const clamped = clampTrackZOffset({
      baseZ,
      desiredOffset,
      prevZ,
      nextZ,
      eps: EPS_Z
    })
    t.zOffset = clamped
    state.tuned = false
    state.saved = false
  },
  removeTrackDepth: (state, { uuid, depth }) => {
    // console.log('rtd', depth, state.tracks[uuid].maxDepth)
    if (!depth || state.tracks[uuid].maxDepth < depth) return
    const strips = Object.values(state.tracks[uuid].strips).map((strip) => {
      if (strip.depth < depth) return strip
      const next = jclone(strip)
      next.depth--
      return next
    })
    state.tracks[uuid].strips = strips.reduce((acc, strip) => {
      acc[strip.uuid] = strip
      return acc
    }, {})
    if (state.tracks[uuid].depth >= depth) state.tracks[uuid].depth -= 1
    state.tracks[uuid].maxDepth -= 1
  },
  // Phase 3: Lane insertion helpers (ultra-expand modal)
  shiftTrackDepths: (state, { uuid, fromDepth = 0, delta = 1 } = {}) => {
    if (!uuid || !state.tracks || !state.tracks[uuid]) return
    const track = state.tracks[uuid]
    const from = Math.max(0, Number(fromDepth) || 0)
    const d = Number(delta) || 0
    if (!Number.isFinite(d) || d === 0) return

    const strips = Object.values(track.strips || {}).map((strip) => {
      if (!strip) return strip
      if ((Number(strip.depth) || 0) < from) return strip
      const next = jclone(strip)
      next.depth = Math.max(0, (Number(next.depth) || 0) + d)
      return next
    }).filter(Boolean)

    track.strips = strips.reduce((acc, strip) => {
      acc[strip.uuid] = strip
      return acc
    }, {})

    // Maintain selected depth lane in UI
    if (Number.isFinite(Number(track.depth)) && Number(track.depth) >= from) {
      track.depth = Math.max(0, Number(track.depth) + d)
    }

    // Bump maxDepth if we shifted the top part of the stack.
    const curMax = Number(track.maxDepth) || 0
    if (curMax >= from) track.maxDepth = Math.max(0, curMax + d)

    state.tuned = false
  },
  ensureTrackMaxDepth: (state, { uuid, depth } = {}) => {
    if (!uuid || !state.tracks || !state.tracks[uuid]) return
    const track = state.tracks[uuid]
    const d = Number(depth)
    if (!Number.isFinite(d)) return
    if ((Number(track.maxDepth) || 0) < d) track.maxDepth = Math.max(0, Math.floor(d))
    state.tuned = false
  },
}

